/**
 * SRV-FG-5-4: Public Service Request Pre-registration Page
 * No auth required — accessible at /public/service-request
 */

import { submitPublicServiceRequest } from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

export function PublicServiceRequestPage() {
  const { t } = useTranslation();
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [equipmentDescription, setEquipmentDescription] = useState('');
  const [problemDescription, setProblemDescription] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!customerName.trim() || customerName.trim().length < 2) {
      setError(t('service.publicRequest.customerName') + ' (min 2)');
      return;
    }
    if (!phone.trim() || phone.trim().length < 5) {
      setError(t('service.publicRequest.phone') + ' (min 5)');
      return;
    }
    if (!equipmentDescription.trim()) {
      setError(t('service.publicRequest.equipmentDescription'));
      return;
    }
    if (!problemDescription.trim() || problemDescription.trim().length < 5) {
      setError(t('service.publicRequest.problemDescription') + ' (min 5)');
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitPublicServiceRequest({
        customerName: customerName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        equipmentDescription: equipmentDescription.trim(),
        problemDescription: problemDescription.trim(),
        preferredDate: preferredDate || undefined,
      });
      setRequestId(result.data.id);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('service.publicRequest.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <svg
              className="h-16 w-16 mx-auto mb-4 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-bold text-green-800 dark:text-green-300 mb-2">
              {t('service.publicRequest.success')}
            </h2>
            {requestId && <p className="text-sm text-muted-foreground mt-2">ID: {requestId}</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t('service.publicRequest.title')}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {t('service.publicRequest.subtitle')}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('service.publicRequest.customerName')} *
              </label>
              <Input
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                placeholder={t('service.publicRequest.customerNamePlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('service.publicRequest.phone')} *
              </label>
              <Input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder={t('service.publicRequest.phonePlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('service.publicRequest.email')}
              </label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('service.publicRequest.emailPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('service.publicRequest.equipmentDescription')} *
              </label>
              <Input
                value={equipmentDescription}
                onChange={e => setEquipmentDescription(e.target.value)}
                placeholder={t('service.publicRequest.equipmentDescriptionPlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('service.publicRequest.problemDescription')} *
              </label>
              <textarea
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-background px-3 py-2 text-sm min-h-[100px]"
                value={problemDescription}
                onChange={e => setProblemDescription(e.target.value)}
                placeholder={t('service.publicRequest.problemDescriptionPlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('service.publicRequest.preferredDate')}
              </label>
              <Input
                type="date"
                value={preferredDate}
                onChange={e => setPreferredDate(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting
                ? t('service.publicRequest.submitting')
                : t('service.publicRequest.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
